import { proxy } from 'valtio';

export enum FeatureFlagSource {
  /** Flags used by our code but not managed by LD or Statsig */
  Local = 'local',
  /** Flags managed by Statsig */
  Statsig = 'statsig',
}

export enum TargetApp {
  CanvasWeb = 'canvas_web',
  DatabasesWeb = 'cc-databases_web',
}

export type ClientFeatureFlagName = keyof typeof defaultFeatureFlagState;
export const isFeatureFlagName = (flag: string): flag is ClientFeatureFlagName => {
  return flag in defaultFeatureFlagState;
};

export type ClientFeatureFlag =
  | {
      enabled: boolean;
      source: FeatureFlagSource.Local;
      targetApp: TargetApp[];
    }
  | {
      enabled: boolean;
      flag: string;
      source: FeatureFlagSource.Statsig;
      targetApp: TargetApp[];
    };
export type ClientFeatureFlags = Record<ClientFeatureFlagName, ClientFeatureFlag>;
/**
 * All feature flags must be disabled by default.
 * @description Default feature flag values cannot be mutated.
 *
 * @remark: You can locally override these values in your development environment by:
 * - adding them to .ff-localdev, which will persist across app restarts
 * - adding the query param ?df=<flagName> to the URL, which works in dev and staging
 * - using the dev-tools panel in local dev
 */
export const defaultFeatureFlagState = {
  /**
   * https://switcheroo.atlassian.com/ui/gates/d9d5f7cd-113c-4af3-b3f8-881336f6a15d/key/cc-whiteboards-protect-historical-preview
   */
  'cc-whiteboards-protect-historical-preview': {
    enabled: false,
    flag: 'cc-whiteboards-protect-historical-preview',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/23c38d46-0999-48b0-b203-0647fbcac9be/key/native_whiteboard_embed
   */
  native_whiteboard_embed: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Waits up to five seconds for linked database entries to arrive after an initial cache miss.
   * https://switcheroo.atlassian.com/ui/gates/f9964eb5-40d2-4809-af6e-b3d37faf67ef/key/cc-databases-entry-links-remote-catch-up
   */
  databasesEntryLinksRemoteCatchUp: {
    enabled: false,
    flag: 'cc-databases-entry-links-remote-catch-up',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Renders authored image fit and offset across Slides and Whiteboards.
   * https://switcheroo.atlassian.com/ui/gates/698fa22f-f598-4cc5-92cf-ada0a62bb581/key/cc-whiteboards-image-content-placement
   */
  imageContentPlacement: {
    enabled: false,
    flag: 'cc-whiteboards-image-content-placement',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Repositions decorations only when the camera scale changes, and only those whose
   * position depends on it, instead of every decoration on every pan and zoom frame.
   * https://switcheroo.atlassian.com/ui/gates/16d1d38f-9d43-4835-9aea-7344ad65ad48/key/cc-whiteboards-decoration-reposition-on-scale
   */
  'cc-whiteboards-decoration-reposition-on-scale': {
    enabled: false,
    flag: 'cc-whiteboards-decoration-reposition-on-scale',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Allows an explicitly installed content viewer host to replace the Confluence bridge.
   * https://switcheroo.atlassian.com/ui/gates/0144c5b2-3cab-45b9-b6be-30a22afb3513/key/cc_content_type_viewer
   */
  cc_content_type_viewer: {
    enabled: false,
    flag: 'cc_content_type_viewer',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Controls the Native Embeds content-ready signal from renderer interactivity.
   * https://switcheroo.atlassian.com/ui/gates/7283f18f-3b14-4025-aee0-b59f81fbb752/key/cc_whiteboards_native_embed_ready_signal
   */
  whiteboardsNativeEmbedReadySignal: {
    enabled: false,
    flag: 'cc_whiteboards_native_embed_ready_signal',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Large board navigation improvements for Whiteboards.
   * https://switcheroo.atlassian.com/ui/gates/520bcbfe-f61c-4360-a039-c54550f06ed6/key/cc-whiteboards-lovability-large-board-nav
   */
  whiteboardsLovabilityLargeBoardNav: {
    enabled: false,
    flag: 'cc-whiteboards-lovability-large-board-nav',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Recovers empty database creates from completion-only documents.
   * https://switcheroo.atlassian.com/ui/gates/9909cc5b-16d4-44e8-9fb4-24c29cd2d08a/key/cc-dbs-hot-307369-fix
   */
  'cc-dbs-hot-307369-fix': {
    enabled: false,
    flag: 'cc-dbs-hot-307369-fix',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Makes Whiteboards comments respect the Confluence site-level reactions setting.
   * https://switcheroo.atlassian.com/ui/gates/e20d8bb2-badd-4323-be52-dce53c5c53d7/key/cc-whiteboards-comment-reactions-site-settings
   */
  commentReactionsRespectSiteSettings: {
    enabled: false,
    flag: 'cc-whiteboards-comment-reactions-site-settings',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the experimental virtualized div-based Table Layout V2.
   * https://switcheroo.atlassian.com/ui/gates/7f6391e0-0252-4ab4-b3a1-2acf6de6405e/key/databases-frontend-table-layout-v2-experiment
   */
  databasesTableLayoutV2: {
    enabled: false,
    flag: 'databases-frontend-table-layout-v2-experiment',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.DatabasesWeb],
  },

  /**
   * Gates Jira issue import idempotency and deduplication by importId and issue key in Whiteboards (CNS-36084).
   * https://switcheroo.atlassian.com/ui/gates/1d31b5dc-3735-4764-9a42-015ec4491814/key/cc-whiteboards-jira-import-idempotency
   */
  jiraImportIdempotency: {
    enabled: false,
    flag: 'cc-whiteboards-jira-import-idempotency',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the queued per-frame Jira unfurl creation path.
   * When enabled, Jira unfurl elements are created via the new batched
   * per-frame queue. When disabled, the legacy unbatched creation path is used.
   * https://switcheroo.atlassian.com/ui/gates/6fab550c-1323-470a-97d2-63075775849e/key/cc-whiteboards-jira-unfurl-creation-batching
   */
  jiraUnfurlCreationBatching: {
    enabled: false,
    flag: 'cc-whiteboards-jira-unfurl-creation-batching',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Defers initial mindmap bootstrap until the ECS execute phase during batched hydration.
   * https://switcheroo.atlassian.com/ui/gates/90095f07-5bc7-47bc-a97b-01c4610056ee/key/cc-whiteboards-mindmap-batched-hydration-fix
   */
  mindmapBatchedHydrationFix: {
    enabled: false,
    flag: 'cc-whiteboards-mindmap-batched-hydration-fix',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables improved Miro and Mural PDF-import guidance, confirmation, and progress messaging.
   * https://switcheroo.atlassian.com/ui/gates/8b7e1fe9-f8bf-4df1-ba61-c9b26fc2c2ce/key/cc-whiteboards-pdf-fidelity-import-ux
   */
  pdfFidelityImportUx: {
    enabled: false,
    flag: 'cc-whiteboards-pdf-fidelity-import-ux',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Gates the large-board UX additions. Two behaviours ride on this gate, so
   * turning it off removes both:
   *
   * - feedback links on element-limit snackbars
   * - the snackbar reporting that a board is still loading its first element
   *   (`CollabBoardLoadingSnackbarSystem`)
   *
   * https://switcheroo.atlassian.com/ui/gates/80d5ae2c-5160-47ae-b837-bb02fa6e5f08/key/cc-whiteboards-large-board-feedback
   */
  largeBoardFeedback: {
    enabled: false,
    flag: 'cc-whiteboards-large-board-feedback',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Disables MUI Slides when the separate slides opt-out gate is enabled.
   * https://switcheroo.atlassian.com/ui/gates/aeac5321-5f57-4a57-aaf7-0cfdf367b888/key/cc-mui-slides-opted-out
   */
  muiSlidesOptedOut: {
    enabled: false,
    flag: 'cc-mui-slides-opted-out',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables applying colours to individual text selections in Whiteboards textboxes.
   * https://switcheroo.atlassian.com/ui/gates/3d117018-c615-4d68-a74d-f1daf3677a9d/key/cc-whiteboards-textbox-inline-colors
   */
  textboxInlineColors: {
    enabled: false,
    flag: 'cc-whiteboards-textbox-inline-colors',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Rejects Yjs updates that reference a client id above `Number.MAX_SAFE_INTEGER`
   * instead of applying them. Such an id cannot be re-encoded by Yjs, so one
   * update carrying it poisons the document for every later apply and encode.
   *
   * Full-stack gate — the server half is
   * `StatsigFeatureGates.unsafeYjsClientIdGuard`.
   *
   * https://switcheroo.atlassian.com/ui/gates/5fd618bf-8103-4dc2-9876-964a1e9eaa88/key/cns-35928_-_reject_unsafe_ids_in_yjs_steps
   */
  unsafeYjsClientIdGuard: {
    enabled: false,
    flag: 'cns-35928_-_reject_unsafe_ids_in_yjs_steps',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * This flag enables/disables hard breaks in the canvas editor
   */
  canvasEditorHardBreak: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Canvas Server passes the screenshot-only rollout value through a preview URL override.
   * When on, the screenshot settle loop resolves after consecutive quiet frames instead of
   * a fixed 3 s window.
   * https://switcheroo.atlassian.com/ui/gates/38f1c466-85db-41df-8452-6c42dabe13af/key/cc-whiteboards-enable-fast-screenshot-settle
   */
  fastScreenshotSettle: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Kill switch for capturing a pasted/dropped DataTransfer's files and items
   * synchronously, before any `await`. When off, reverts to reading them at their
   * original (later) call sites, which Firefox can silently invalidate.
   */
  clipboardPasteSyncCapture: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * This flag enables/disables the use of style resolution in the render manager.
   * https://switcheroo.atlassian.com/ui/gates/ae78b1c5-d387-4d42-ad0b-1843818f6378/key/use-style-resolution-in-render-manager
   */
  useStyleResolutionInRenderManager: {
    enabled: false,
    flag: 'use-style-resolution-in-render-manager',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Org-targeted gate for the Collaboration-Context-scoped accessible products
   * rollout. When this gate (or {@link experimentalAccessibleProductsCloudId}) is on
   * and the org has units isolation enforced, the site service fetches accessible
   * products from `/experimental/v2/accessible-products` instead of the legacy
   * site-scoped `/accessible-products` endpoint.
   * https://switcheroo.atlassian.com/ui/gates/3525ca93-3b88-4ad9-92a2-c194dae25efc/key/canvas-experimental-accessible-products
   */
  experimentalAccessibleProducts: {
    enabled: false,
    flag: 'canvas-experimental-accessible-products',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Cloud ID (per-site) targeted companion to {@link experimentalAccessibleProducts}.
   * Either gate opts the site into the units-aware accessible products behaviour.
   * https://switcheroo.atlassian.com/ui/gates/93e560d2-35b7-4ad3-9083-1c784e89fb8e/key/canvas-experimental-accessible-products-cloud-id
   */
  experimentalAccessibleProductsCloudId: {
    enabled: false,
    flag: 'canvas-experimental-accessible-products-cloud-id',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Units GA rollout master gate, owned by the Nexus team and shared across products.
   * Canvas treats it as the killswitch that must be on before any units isolation
   * behaviour (such as the unit-scoped accessible products endpoint) is considered.
   * https://switcheroo.atlassian.com/ui/gates/e9056e3c-e0a4-4d70-9d81-ee8a84f92dff/key/cc-units-ga
   */
  ccUnitsGa: {
    enabled: false,
    flag: 'cc-units-ga',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Used for testing purposes. Do not use otherwise.
   * @deprecated
   */
  test: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables native media files (video, audio, documents, archives, data)
   * in Whiteboards. When OFF, the @canvas/module-media module reports
   * isEnabled=false so it is not loaded into any mode and no wiring
   * (element-type registration, importer routing, clipboard predicates,
   * file-picker accept lists) takes effect at runtime.
   *
   * Pre-existing image and PDF behaviour is unaffected.
   *
   * TODO: add statsig flag
   */
  whiteboardsNativeMedia: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables agent presence participation and facepile rendering in Whiteboards
   * and Databases for agent content interactions.
   * Gate: https://switcheroo.atlassian.com/ui/gates/ae6b62b8-c7be-492e-b436-6336fa081146/key/canvas_agent_presence_facepile_frontend
   */
  canvasAgentPresenceFacepileFrontend: {
    enabled: false,
    source: FeatureFlagSource.Statsig,
    flag: 'canvas_agent_presence_facepile_frontend',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Prefers hydrated Identity avatars over temporary artwork for agent telepointers.
   * Gate: https://switcheroo.atlassian.com/ui/gates/69535c4f-19ea-4e1f-9c95-085784e5c87d/key/canvas-agent-telepointer-identity-avatars
   */
  canvasAgentTelepointerIdentityAvatars: {
    enabled: false,
    source: FeatureFlagSource.Statsig,
    flag: 'canvas-agent-telepointer-identity-avatars',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Preserves completed frontend Rovo cursors and selection borders briefly,
   * independently of the facepile rollout.
   */
  rovoTelepointerDwell: {
    enabled: false,
    source: FeatureFlagSource.Statsig,
    flag: 'cc-whiteboards-rovo-telepointer-dwell',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables adding debug report to sentry errors
   */
  shouldAddDebugReportToSentryErrors: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Turns on using zoom CSS field in the underlay system (better layout
   * calculations) rather than transforms
   */
  useZoomCssInUnderlaySystem: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Uses explicit SPA navigation refactor instead of relying on token change.
   *
   */
  explicitSPANavigation: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the AI Smart Button and connection to the Confluence right panel rendering agent capabilities
   * https://switcheroo.atlassian.com/ui/gates/fa7497cc-b87f-483b-b6ea-e1457671c9f0/key/should-enable-whiteboard-ai-smart-button
   */
  aiSmartButton: {
    enabled: false,
    flag: 'should-enable-whiteboard-ai-smart-button',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enabled a low quality mode if only software rendering is available
   * https://switcheroo.atlassian.com/ui/gates/c035322d-aff5-4590-8987-d81e85669762/key/whiteboard-should-enable-low-quality-mode
   */
  lowQualityMode: {
    enabled: false,
    flag: 'whiteboard-should-enable-low-quality-mode',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the "retry" button for AI clustering/smart grouping feature.
   *
   * Should only be enabled once AI clustering supports responding with a
   * different cluster on each request
   * https://switcheroo.atlassian.com/ui/gates/8a208f5b-9818-4be1-9532-4f606e493779/key/should_enable_ai_clustering_retry_button
   */
  aiClusteringRetry: {
    enabled: false,
    flag: 'should_enable_ai_clustering_retry_button',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Makes it so that section titles remain the same size regardless of the
   * camera scale
   */
  cameraAgnosticSectionTitles: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the accessibility module containing canvas as DOM, and other a11y features.
   * https://switcheroo.atlassian.com/ui/gates/aa2aba2d-0060-4b20-86e9-d3d44184369d/key/a11y-module
   */
  a11yModule: {
    enabled: false,
    flag: 'a11y-module',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the locale switcher, which is ONLY for whiteboards developers.
   * https://switcheroo.atlassian.com/ui/gates/ba151294-55cc-49c9-8b2a-af2f65e9a42e/key/enable_whiteboards_locale_switcher
   */
  enableWhiteboardsLocaleSwitcher: {
    enabled: false,
    flag: 'enable_whiteboards_locale_switcher',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the visual refresh for atlaskit components and colors
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/05b28d4c-67f1-45db-a0c6-4c14ec40185c/key/platform-component-visual-refresh
   */
  visualRefreshComponentsAndColors: {
    enabled: false,
    flag: 'platform-component-visual-refresh',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the visual refresh for platform icons.
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/e36c4a44-2846-4b76-b262-40e1d1e3932e/key/platform-visual-refresh-icons
   */
  visualRefreshIcons: {
    enabled: true,
    flag: 'platform-visual-refresh-icons',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Whether or not to proceed with using the dirty status of the whiteboard, and whether or not to proceed with attempting to properly clean up the dirty board
   * https://switcheroo.atlassian.com/ui/gates/aa6287b7-6739-445d-b312-06ae6446f3e2/key/clean_dirty_whiteboard
   */
  cleanDirtyWhiteboard: {
    enabled: false,
    flag: 'clean_dirty_whiteboard',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /*
   * Enables content wrapper - header in confluence
   * https://switcheroo.atlassian.com/ui/gates/86ef3e79-79a9-4799-b061-9e7f0cf3f36b/key/confluence_frontend_object_header
   */
  confluenceContentWrapperHeader: {
    enabled: false,
    flag: 'confluence_frontend_object_header',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Exposes mindmaps as a feature
   * https://switcheroo.atlassian.com/ui/gates/659f56de-ea80-482e-a9f2-ae96b90e0fea/key/should_enable_mindmaps
   */
  mindmaps: {
    enabled: false,
    flag: 'should_enable_mindmaps',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/f21fd329-b4ee-40bb-8fe3-1505c9ddd73c/key/dev_socket_disconnect
   */
  devSocket: {
    enabled: false,
    flag: 'dev_socket_disconnect',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Updates media field settings for Field Settings 2.0 project
   *
   * https://switcheroo.atlassian.com/ui/gates/f5ff837f-5fd3-4049-9775-7af3c276b4fd/key/database-field-setting-2-media
   */
  mediaFieldSettings: {
    enabled: false,
    flag: 'database-field-setting-2-media',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Allows users to switch to an inline view for smart links
   */
  inlineViewForSmartLinks: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the use of content wrapper refresh in confluence
   * See: https://hello.atlassian.net/wiki/spaces/CAN/pages/5048306970/Feature+Content+wrapper+for+whiteboards#%F0%9F%96%BC%EF%B8%8F-The-feature
   *
   * https://switcheroo.atlassian.com/ui/gates/d4632be3-73c3-4ab2-babb-0ae7d7d07f19/key/should_use_content_wrapper_refresh
   * See https://switcheroo.atlassian.com/ui/gates/ece5dbb1-5afd-4666-8702-8f346a8731f7/key/confluence_frontend_content_wrapper
   */
  shouldUseContentWrapperRefresh: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables frame throttling for underlay dom elements like smart links
   */
  domFrameThrottling: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables switcheroo dev ui for creating/inspecting/updating health of experiments
   */
  switcherooObserver: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables consolidated comments
   *
   * https://switcheroo.atlassian.com/ui/gates/996d045e-c24d-4b68-a73a-2eac3dae01a2/key/cc-whiteboards-consolidating-comments
   */
  consolidatedComments: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the media for comments
   *
   * https://switcheroo.atlassian.com/ui/gates/dcc431e8-ddbc-4ba4-bda0-07d8ed0a07e9/key/cc-whiteboards-consolidating-comments-media
   */
  commentsMedia: {
    enabled: false,
    flag: 'cc-whiteboards-consolidating-comments-media',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/05649fc3-e17a-4848-9e4f-a2c52c057485/key/cc-whiteboards-consolidating-comments-unread
   */
  unreadComments: {
    enabled: false,
    flag: 'cc-whiteboards-consolidating-comments-unread',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the diagramming agent
   * https://switcheroo.atlassian.com/ui/gates/7a062391-adbd-427c-9b4c-3243921847b3/key/diagramming_agent_for_whiteboards
   */
  diagrammingAgent: {
    enabled: false,
    flag: 'diagramming_agent_for_whiteboards',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables frontend handling for content created by a remote Confluence agent,
   * including atomic update buffering and undo/redo capture.
   * https://switcheroo.atlassian.com/ui/gates/ae31f983-844c-4c2f-8854-389c12ddc451/key/cc-whiteboards-agent-edit-ui
   */
  agenticConfluenceFrontendEditUI: {
    enabled: false,
    flag: 'cc-whiteboards-agent-edit-ui',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Feature flag for #hot-120681
   * https://switcheroo.atlassian.com/ui/gates/03cae3dc-47a5-4315-9ffc-905d4fdca210/key/whiteboards_connection_fallback
   */
  useConnectionFallback: {
    enabled: false,
    flag: 'whiteboards_connection_fallback',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables lazy sync during connection flow, don't need to do handshake immediately
   * https://switcheroo.atlassian.com/ui/gates/68fc5cf4-8646-4ed9-a25a-31282fa5faa1/key/canvas_lazy_sync_handshake
   */
  lazySyncHandshake: {
    enabled: false,
    flag: 'canvas_lazy_sync_handshake',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables the new look for the main toolbar for content wrapper work
   * https://switcheroo.atlassian.com/ui/gates/da643c23-584e-4d7e-bf7b-ced0c5cbca78/key/content_wrapper_main_toolbar_refresh
   * Fully rolled out to all users. The default is set to true until the old toolbar is removed.
   * See https://switcheroo.atlassian.com/ui/gates/ece5dbb1-5afd-4666-8702-8f346a8731f7/key/confluence_frontend_content_wrapper
   */
  contentWrapperMainToolbarRefresh: {
    enabled: true,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Uses Convo AI endpoint for AI Stickies
   * https://switcheroo.atlassian.com/ui/gates/563d5403-1387-4cff-a6f7-f8ec80b56892/key/migrate_ai_teammate_to_convo_ai
   */
  migrateAiTeammateToConvoAI: {
    enabled: false,
    flag: 'migrate_ai_teammate_to_convo_ai',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables v2 brainstorming agent API
   * https://switcheroo.atlassian.com/ui/gates/3cd9c598-4cf2-4ea3-b28c-f477b5cccbb7/key/should_use_v2_brainstorming_agent_api
   */
  useV2BrainstormingAgentAPI: {
    enabled: false,
    flag: 'should_use_v2_brainstorming_agent_api',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the new element toolbar changes.
   * https://switcheroo.atlassian.com/ui/gates/c5ff782f-5c96-495d-a597-e23cbb481219/key/cc-whiteboards-new-element-toolbar
   */
  newElementToolbar: {
    enabled: false,
    flag: 'cc-whiteboards-new-element-toolbar',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Native Database embed milestone flags
   * These flags correspond to the milestones defined in the Database Visual Refresh project.
   */

  /**
   * M1b Hello release: Toolbar/Action Bar
   * Covers:
   * - Database search filtering
   * - Embed settings panel
   * - Sort panel
   * - Filter panel
   * - Hide fields panel
   * - Database templates & import configuration
   * - WBs Element Toolbar → DBs Action Bar: Cell Interactions
   * - View/Change views from element toolbar
   * - Layout panel
   *
   * @see https://hello.atlassian.net/wiki/spaces/CD2/pages/4737992889/Feature+New+native+embed+whiteboards+pages
   */
  wbDbToolbarActionBar: {
    targetApp: [TargetApp.CanvasWeb],
    source: FeatureFlagSource.Local,
    enabled: false,
  },

  /**
   * M1c: Hello release: Flags/Snackbars/Modals
   * Covers:
   * - Snackbars shown in Whiteboards location
   * - Save changes button on DB embed
   * - Flags/section messages shown in Whiteboards location
   */
  wbDbFlagsSnackbarsModals: {
    targetApp: [TargetApp.CanvasWeb],
    source: FeatureFlagSource.Local,
    enabled: false,
  },

  /**
   * M1d: Hello release: Other
   * Covers:
   * - Best effort initial and dynamic sizing
   * - E2E tests, performance tests, SLOs, integration tests
   * - Value & row entries overhead
   * - Create empty database from Whiteboards
   * - Experimentation analytics
   * @see https://hello.atlassian.net/wiki/spaces/CD2/pages/4737992889/Feature+New+native+embed+whiteboards+pages
   */
  wbDbHelloReleaseOther: {
    targetApp: [TargetApp.CanvasWeb],
    source: FeatureFlagSource.Local,
    enabled: false,
  },

  /**
   * M1e: Customer release: Change-boarding
   * Covers:
   * - 'New' indicator on databases element in whiteboards toolbar
   * - User awareness of new functionality
   * @see https://hello.atlassian.net/wiki/spaces/CD2/pages/4737992889/Feature+New+native+embed+whiteboards+pages
   */
  wbDbChangeboarding: {
    targetApp: [TargetApp.CanvasWeb],
    source: FeatureFlagSource.Local,
    enabled: false,
  },
  /**
   * Enables ECS loop performance monitoring and reporting to UFO
   * https://switcheroo.atlassian.com/ui/gates/8c00a7c0-f780-483b-b823-3e6ceca37e70/key/cc-whiteboards-ecs-loop-perf
   */
  ecsLoopPerf: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-ecs-loop-perf',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/e36c4a44-2846-4b76-b262-40e1d1e3932e/key/platform-visual-refresh-icons
   */
  'platform-visual-refresh-icons': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'platform-visual-refresh-icons',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Enables the experimental pill-based view tabs in Databases.
   * https://switcheroo.atlassian.com/ui/gates/6b156649-d499-404b-84e1-8c52d5c907df/key/databases-pill-view-tabs
   */
  databasesPillViewTabs: {
    enabled: false,
    flag: 'databases-pill-view-tabs',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/05b28d4c-67f1-45db-a0c6-4c14ec40185c/key/platform-component-visual-refresh
   */
  'platform-component-visual-refresh': {
    source: FeatureFlagSource.Statsig,
    enabled: true,
    flag: 'platform-component-visual-refresh',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/5d38d392-5b8d-46ed-beb4-43b5d62db8f9/key/confluence_frontend_rovo_db_referencing_content
   */
  'databases-rovo-referencing-content': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'confluence_frontend_rovo_db_referencing_content',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Migrates database user-value hover cards to @atlassian/user-profile-card.
   * https://switcheroo.atlassian.com/ui/gates/78c0ff8d-7715-487c-8d8e-349361008ec2/key/people-teams_migrate-user-profile-card
   */
  'people-teams_migrate-user-profile-card': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'people-teams_migrate-user-profile-card',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/006917a3-9e2e-4bbc-8f1a-affbcec76685/key/canvas-issue-terminology-refresh-update
   */
  'canvas-issue-terminology-refresh-update': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'canvas-issue-terminology-refresh-update',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/aa50de8c-be28-4f1e-bcff-8cacd1eba833/key/canvas-project-terminology-refresh
   */
  'canvas-project-terminology-refresh': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'canvas-project-terminology-refresh',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables Jira project picker search fixes.
   * https://switcheroo.atlassian.com/ui/gates/68135fb8-8917-410f-8de1-76f0d3dff76a/key/cc-whiteboards-cns-36606
   */
  jiraImporterProjectSearchFixes: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-cns-36606',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Raises Whiteboards selection outlines above overlapping elements.
   * https://switcheroo.atlassian.com/ui/gates/cba04f54-0e21-4be0-8790-0d395f8d41ac/key/cc-whiteboards-selection-outline-on-top
   */
  whiteboardsSelectionOutlineOnTop: {
    enabled: false,
    flag: 'cc-whiteboards-selection-outline-on-top',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables flags to be displayed over the bridge API.
   * https://switcheroo.atlassian.com/ui/gates/9bd09e19-971f-442b-abd4-289d0f8fe4a2/key/database-frontend-flags-over-bridge
   */
  'database-frontend-flags-over-bridge': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'database-frontend-flags-over-bridge',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Uses fast-deep-equal for Databases Redux selector subscriptions.
   * https://switcheroo.atlassian.com/ui/gates/e9b25a31-bf72-42cd-bdd4-47cbe34bd3f5/key/cc-dbs-perf-uplift
   */
  databasePerfUplift: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-dbs-perf-uplift',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Prevents wrapped Smart Link decorations from crossing Database cell bounds.
   * https://product-fabric.atlassian.net/browse/CTD-7259
   * https://switcheroo.atlassian.com/ui/gates/46833542-e0ee-4a28-9ab2-6ad2479a79e6/key/cc-databases-smart-link-parent-bounds
   */
  databaseSmartLinkParentBounds: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-databases-smart-link-parent-bounds',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Uses a foreground-only selected state for Databases toolbar buttons.
   * https://switcheroo.atlassian.com/ui/gates/f2d0ff14-c7f9-489b-a921-0eb7275a16dc/key/databases-frontend-simplify-toolbar-icons
   */
  'databases-frontend-simplify-toolbar-icons': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'databases-frontend-simplify-toolbar-icons',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/fb6fd382-a6f2-4107-8b7c-a19a307bb6b2/key/whiteboards-show-all-diagram-types
   */
  whiteboardsShowAllDiagramTypes: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards-show-all-diagram-types',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * The new approach for updating an element instead of deleting + creating a
   * new one.
   * https://switcheroo.atlassian.com/ui/gates/4c543991-abac-4b9a-916b-f24bf9cb79c1/key/whiteboards_aifc_transform
   */
  whiteboardsAIFCNewTransform: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards_aifc_transform',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables CWR ingestion improvements for table capture, text normalization,
   * and scaled text source bounds.
   * https://switcheroo.atlassian.com/ui/gates/06df8fd6-be6d-4828-81d5-24ccf16c58fd/key/cc-whiteboards-cwr-ingestion-improvements
   */
  cwrIngestionImprovements: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-cwr-ingestion-improvements',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Blank object experience side modal design updates: renders below the
   * toolbars, extends over the bottom-right controls rather than being clipped
   * above them, and scrolls in the viewport at its natural height.
   * https://switcheroo.atlassian.com/ui/gates/03215673-b84e-42d0-9c0a-99a20a0590ee/key/cwr_canvas_boe_design_updates
   */
  cwrCanvasBoeDesignUpdates: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cwr_canvas_boe_design_updates',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Umbrella flag for short-lived Whiteboards hotfixes (e.g. retrying
   * transient 503s on `updateContentTitle`). Enabling this turns on the
   * additional resilience paths gated by it.
   * https://switcheroo.atlassian.com/ui/gates/b4a4f0f1-cfdd-48fc-90da-1cf5d0d7eb98/key/team_26_whiteboards_hotfixes
   */
  team26WhiteboardsHotfixes: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'team_26_whiteboards_hotfixes',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Defers whiteboard Rovo bridge registration until the host frame provides
   * its content identity.
   * https://switcheroo.atlassian.com/ui/gates/a4b19a7d-eb15-4a32-accd-c6ee3beb3277/key/cc-wbs-fix-missing-rovo-content-bridge
   */
  fixMissingRovoContentBridge: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-wbs-fix-missing-rovo-content-bridge',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Rolls back the Canvas-side native embed loading lifecycle notifications
   * without disabling the underlying AIFC stream correlation fixes.
   *
   * Shares the AFM gate `platform_remix_whiteboard_loading_killswitch`.
   * https://switcheroo.atlassian.com/ui/gates/c2c85264-2c3e-463c-aa62-89542de828f5/key/platform_remix_whiteboard_loading_killswitch
   */
  remixWhiteboardLoadingKillSwitch: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'platform_remix_whiteboard_loading_killswitch',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * A pseudo-feature-flag/segment that is true for Fabric Team Members
   * production/staging and false for everyone else. Designed to be used in
   * tandem with other feature flags.
   * https://switcheroo.atlassian.com/ui/gates/cb44ff26-177b-4c03-849c-b9447ec61881/key/cc-whiteboards-internal-users
   */
  internalUsers: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-internal-users',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables BRIE migration-placeholder handling and omitted-media notices.
   * https://switcheroo.atlassian.com/ui/gates/2140c065-7130-4570-a8f8-83a7fd0bd1b9/key/cc-whiteboards-ui-brie-improvements
   */
  uiBrieImprovements: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-ui-brie-improvements',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables native embed bridge initialization.
   * https://switcheroo.atlassian.com/ui/gates/b4c59c3e-a473-43c6-b576-0d560568a263/key/database-frontend-native-embed-bridge
   */
  'database-frontend-native-embed-bridge': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'database-frontend-native-embed-bridge',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/c5ff782f-5c96-495d-a597-e23cbb481219/key/cc-whiteboards-new-element-toolbar
   */
  'cc-whiteboards-new-element-toolbar': {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-new-element-toolbar',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables updated UI for ADS lozenge, tag, and badge components.
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/195b5837-cf3f-4b7b-acf6-f755156b849a/key/platform-dst-lozenge-tag-badge-visual-uplifts
   */
  platformLozengeTagBadgeVisualUplifts: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'platform-dst-lozenge-tag-badge-visual-uplifts',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables the shape token theme by default.
   *
   * NOTE: This is NOT a canvas specific feature gate, this gate is used by
   * multiple products and teams. Take EXTREME CARE when updating this flag.
   *
   * https://switcheroo.atlassian.com/ui/gates/7d3cc87f-78fe-4ed1-a528-69b2f6eb937f/key/platform-dst-shape-theme-default
   */
  platformShapeThemeDefault: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'platform-dst-shape-theme-default',
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Shared Design System gate for Finesse color and typography themes.
   * Rollout changes affect multiple products; coordinate with the gate owners.
   *
   * https://switcheroo.atlassian.com/ui/gates/d87f991b-9a0c-4229-a6e4-8a5b8050b952/key/platform-dst-tokens-finesse
   */
  platformTokensFinesse: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'platform-dst-tokens-finesse',
    targetApp: [TargetApp.DatabasesWeb],
  },
  /**
   * Smart Link feature flags required for removing patch -
   * https://switcheroo.atlassian.com/ui/gates/2aed53b3-6766-4834-aafb-15ac38fe9586/key/platform_linking_enable_card_preview_action_size
   * https://switcheroo.atlassian.com/ui/gates/f39f84c4-01ec-43a4-a0c1-3b6de5930ce1/key/linking_platform_hide_preview_action_blanket_prop
   * Setting these to local as we would never want these flags to be disabled in canvas.
   * The purpose of these flags is to turn on the smart link preview action size and hidden blanket props in the smart link component -
   * these props are not used by any other known product, hence these flags in case other products want to use them.
   * (turning these on would result in an strange experience with the blanket only applying to the canvas iframe)
   */
  platform_linking_enable_card_preview_action_size: {
    source: FeatureFlagSource.Local,
    enabled: true,
    targetApp: [TargetApp.DatabasesWeb, TargetApp.CanvasWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/f39f84c4-01ec-43a4-a0c1-3b6de5930ce1/key/linking_platform_hide_preview_action_blanket_prop
   */
  linking_platform_hide_preview_action_blanket_prop: {
    source: FeatureFlagSource.Local,
    enabled: true,
    targetApp: [TargetApp.DatabasesWeb, TargetApp.CanvasWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/01bf5d3a-23dd-4a10-99e6-5d90dc5deac8/key/platform_deprecate_lp_cc_embed
   */
  platform_deprecate_lp_cc_embed: {
    enabled: false,
    flag: 'platform_deprecate_lp_cc_embed',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.DatabasesWeb, TargetApp.CanvasWeb],
  },
  /**
   * Gate for template insertion contextual UI side effects, i.e. open shape main toolbar
   * or sidebar pending underlying variant - confusingly named,
   * as it currently gates the experiments-funnel module
   * https://switcheroo.atlassian.com/ui/gates/3e9f7182-2cbc-4a6b-890f-5b2adfe9ee36/key/whiteboards-contextual-shapes-gate
   */
  whiteboardsContextualShapesGate: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards-contextual-shapes-gate',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the use of new Rovo mini modal options, which requires canvas to stop handling certain Smart create options.
   * https://switcheroo.atlassian.com/ui/gates/57ad995f-ecbb-4425-b178-374a71d08b48/key/cc-whiteboards-override-rovo-mini-modal-options
   * */
  rovoMiniModalOptionsOverride: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-override-rovo-mini-modal-options',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Offers Rovo and Studio agents in whiteboard comment mentions, and tells Confluence when a whiteboard comment saves so it can start the agent.
   * https://switcheroo.atlassian.com/ui/gates/419d83a1-1921-4b31-b052-6ee8e8997701/key/canvas-rovo-comment-sessions
   */
  rovoCommentSessions: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'canvas-rovo-comment-sessions',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables whiteboard context size optimisation to allow large whiteboard context to be edited via AIFC
   * https://switcheroo.atlassian.com/ui/gates/264e9d73-8d29-4c48-bbe5-4d6d53ad453c/key/whiteboards-context-size-optimisation
   * */
  whiteboardContextOptimisation: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards-context-size-optimisation',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables minification of SVG content
   * https://switcheroo.atlassian.com/ui/gates/8b651a50-bea8-40b0-989b-9cf3e02c6026/key/whiteboards_minify_aifc
   */
  minifySVG: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'whiteboards_minify_aifc',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Marks legitimately overlapping elements with `allow-overlap="true"` in the SVG
   * given to the LLM as whiteboard context, so AIFC constraint solving does not
   * pull intentionally overlapping elements apart when the board is AI edited.
   * https://switcheroo.atlassian.com/ui/gates/a80009db-4b10-446f-a2fa-8a1404e6215d/key/cns-36835_allow-overlap_on_export
   */
  aifcPreserveExistingOverlaps: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cns-36835_allow-overlap_on_export',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Controls whether race mode can run on mobile clients
   * https://switcheroo.atlassian.com/ui/gates/4f8f07d5-b028-490d-bcbe-f592cdcb2ac3/key/race_mode_enable_mobile
   */
  raceModeEnableMobile: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'race_mode_enable_mobile',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the Atlassian architecture icons in the cloud architecture section
   * https://switcheroo.atlassian.com/ui/gates/864d20fc-4d7b-4f37-82eb-8e46632381ab/key/cc-whiteboards-atlassian-architecture-icons
   */
  atlassianArchitectureIcons: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-atlassian-architecture-icons',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enabled path dodging in AIFC
   * https://switcheroo.atlassian.com/ui/gates/1db8edaf-c35e-47e0-a82e-ce0e72bf822b/key/cc-whiteboards-ai-diagramming-path-dodging
   */
  aiPathDodging: {
    enabled: false,
    flag: 'cc-whiteboards-ai-diagramming-path-dodging',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enable A11Y changes for databases
   * https://switcheroo.atlassian.com/ui/gates/868c38b1-65b0-4ab8-a00f-a8860bc5d3ad/key/a11y_whiteboards_changes
   */
  a11yWhiteboardsChanges: {
    enabled: false,
    flag: 'a11y_whiteboards_changes',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables performance optimizations for UML composite shape rendering and dragging.
   * Gate must be created in Switcheroo for canvas_web.
   * https://switcheroo.atlassian.com/ui/gates/ff94913b-b74a-4f93-8016-f5d6e05f79bb/key/cc-whiteboards-uml-shapes-performance
   */
  umlShapesPerformance: {
    enabled: false,
    flag: 'cc-whiteboards-uml-shapes-performance',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the improved copy paste features where pasted text is formatted and pasting text has the paste options toolbar appear.
   * https://switcheroo.atlassian.com/ui/gates/d18df6e5-6497-41e9-b6e1-3623cf8605ff/key/cc-whiteboards-improved-copy-paste-ff
   */
  improvedCopyPasteFlag: {
    enabled: true,
    flag: 'cc-whiteboards-improved-copy-paste-ff',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * https://switcheroo.atlassian.com/ui/gates/bf310873-d9d6-4d4c-9583-377fb0f3ed66/key/aifc_copy_analytics
   */
  aifcCopyAnalytics: {
    enabled: false,
    flag: 'aifc_copy_analytics',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables the Atlaskit scrollbar harmonisation reset in first-party guest apps.
   * https://switcheroo.atlassian.com/ui/gates/11306011-9cc9-40c4-b010-a5b306534bf1/key/platform_dst_scrollbar_harmonisation
   */
  scrollbarHarmonisation: {
    enabled: false,
    flag: 'platform_dst_scrollbar_harmonisation',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Enables the parallel v2 agentic pipeline for slides create/edit/offline flows.
   * The flag is intentionally dormant until slides routes v2 at a single boundary.
   */
  slidesAgentV2: {
    enabled: false,
    source: FeatureFlagSource.Local,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Gates completed-action recovery independently of existing Slides reliability fixes.
   * https://switcheroo.atlassian.com/ui/gates/d7981e0d-ee3d-49e5-9692-a4e330e51c7c/key/cc-slides-restore-blank-or-partial-slides
   */
  slidesRestoreBlankOrPartialSlides: {
    enabled: false,
    flag: 'cc-slides-restore-blank-or-partial-slides',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards R0 UX Reliability fixes for slides.
   * https://switcheroo.atlassian.com/ui/gates/4c65c091-b4c0-4a47-9c59-61faec39d4e4/key/cc-r0-slides-reliability
   */
  slidesReliabilityR0: {
    enabled: false,
    flag: 'cc-r0-slides-reliability',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards improvements from Lovability Thanksgiving stream for Confluence Slides.
   * https://switcheroo.atlassian.com/ui/gates/ebeaa471-401a-418a-b7df-c5c3b5d15680/key/cc-mui-slides-lovability
   */
  slidesLovability: {
    enabled: false,
    flag: 'cc-mui-slides-lovability',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards R1 lovability improvements for Confluence Slides.
   * https://switcheroo.atlassian.com/ui/gates/0a41a8e9-dcb9-4213-8cf9-4e89cea8a976/key/cc-r1-slides-lovability
   */
  slidesLovabilityR1: {
    enabled: false,
    flag: 'cc-r1-slides-lovability',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards R2 lovability improvements for Confluence Slides.
   * https://switcheroo.atlassian.com/ui/gates/06668a73-9e03-4e52-8db4-f48290ef7354/key/cc-r2-slides-lovability
   */
  slidesLovabilityR2: {
    enabled: false,
    flag: 'cc-r2-slides-lovability',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards the refactor of slides ingestion to mirror AFC ingestion to support MCP for slides.
   * https://switcheroo.atlassian.com/ui/gates/33a300c7-4a6e-4e25-8f23-95800c33ab2d/key/cc-mui-slides-ingestion-refactor
   */
  isomorphicSlidesIngestion: {
    enabled: false,
    flag: 'cc-mui-slides-ingestion-refactor',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Guards quality fixes for Confluence slides for the 10% release.
   * https://switcheroo.atlassian.com/ui/gates/f8d42243-10f4-490c-ac73-7650ca71b017/key/cc-mui-slides-quality-10-percent
   */
  slidesQuality10Percent: {
    enabled: false,
    flag: 'cc-mui-slides-quality-10-percent',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Allows font-family styles through the Slides ingestion and read paths.
   * https://switcheroo.atlassian.com/ui/gates/3756b741-361d-405b-abe7-707da36194c3/key/cc-mui-slides-quality-uplift
   */
  slidesQualityUplift: {
    enabled: false,
    flag: 'cc-mui-slides-quality-uplift',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables shared-world-space initialization for new Slides documents and acts as the
   * master prerequisite for legacy document migration. Persisted document state controls
   * runtime behavior.
   * https://switcheroo.atlassian.com/ui/gates/1782ca93-d9f0-4e6a-93a9-e3bba4dd8d21/key/cc-mui-slides-shared-world-space
   */
  slidesSharedWorldSpace: {
    enabled: false,
    flag: 'cc-mui-slides-shared-world-space',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables migration of legacy Slides documents when the shared-world-space master gate is
   * also enabled.
   * https://switcheroo.atlassian.com/ui/gates/c02ce1a4-e120-4611-9d5f-c1ce905f83df/key/cc-mui-slides-shared-world-space-enable-migration
   */
  slidesSharedWorldSpaceEnableMigration: {
    enabled: false,
    flag: 'cc-mui-slides-shared-world-space-enable-migration',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Runs legacy Slides shared-world-space migration planning without mutating the document.
   * https://switcheroo.atlassian.com/ui/gates/1b4782d4-1153-4d5c-8f69-032dd0f92767/key/cc-mui-slides-shared-world-space-migration-dry-run
   */
  slidesSharedWorldSpaceMigrationDryRun: {
    enabled: false,
    flag: 'cc-mui-slides-shared-world-space-migration-dry-run',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables cross-deck copy paste feature for slides
   * https://switcheroo.atlassian.com/ui/gates/062f1a45-119d-4513-b2f4-b05800dee1ff/key/cc_slides_copy_paste
   */
  slidesCopyPaste: {
    enabled: false,
    flag: 'cc_slides_copy_paste',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables the UI to show for the cross flow touchpoint from whiteboards to JPD
   * https://switcheroo.atlassian.com/ui/gates/1faf7547-d0ca-4673-abc3-ffc4dce3ed96/key/jpdgrw-147_whiteboards_to_jpd_killswitch
   */
  jpdWhiteboardsKillSwitch: {
    enabled: false,
    flag: 'jpdgrw-147_whiteboards_to_jpd_killswitch',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Migrates the Confluence Object Sidebar (comments and details panels) from a custom floating
   * panel implementation to the @atlassian/panel-system PanelContainer. When enabled, the sidebar
   * is rendered in the ADS navigation-system Panel slot (outside the canvas iframe),
   * which pushes the iframe narrower rather than overlaying it. As a result, the canvas should NOT
   * open its internal CanvasDrawerPanel for external panels (commentsPanel, detailsPanel), and
   * decoration position offsets for the sidebar width are not needed.
   * https://switcheroo.atlassian.com/ui/gates/3974d23d-a3c1-43cb-870d-78c0eb3d496d/key/ads-panel-system-confluence-object-sidebar
   */
  adsPanelSystemConfluenceObjectSidebar: {
    enabled: false,
    flag: 'ads-panel-system-confluence-object-sidebar',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables table interaction improvements in Whiteboards.
   * https://switcheroo.atlassian.com/ui/gates/029001c6-7325-41c3-b06d-92231272c241/key/cc-whiteboards-tables-improvements
   */
  whiteboardsTablesImprovements: {
    enabled: false,
    flag: 'cc-whiteboards-tables-improvements',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables consistent table geometry and layout across collaborators.
   * https://switcheroo.atlassian.com/ui/gates/fe230bc7-3213-441f-8f8d-e4479ed00d11/key/cc-whiteboards-tables-remote-improvements
   */
  whiteboardsTablesRemoteImprovements: {
    enabled: false,
    flag: 'cc-whiteboards-tables-remote-improvements',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Establishes table-cell containment for imported content after layout.
   * https://switcheroo.atlassian.com/ui/gates/768d44b0-c9af-4b33-b393-be8edf2ba765/key/cc-whiteboards-tables-adopt-into-table-on-import
   */
  whiteboardsTablesAdoptIntoTableOnImport: {
    enabled: false,
    flag: 'cc-whiteboards-tables-adopt-into-table-on-import',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Uses actual table-cell ownership when copying or duplicating tables (CNS-37330).
   * https://switcheroo.atlassian.com/ui/gates/58c7a197-9faf-42d1-b74f-1e9e52dcadea/key/cc-whiteboards-tables-clipboard-ownership
   */
  whiteboardsTablesClipboardOwnership: {
    enabled: false,
    flag: 'cc-whiteboards-tables-clipboard-ownership',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Clears selected table cells instead of deleting their structural entities.
   * https://switcheroo.atlassian.com/ui/gates/713bc8fb-035d-47a8-9381-e23bae6d1da7/key/cc-whiteboards-tables-multiselect-deletion-fix
   */
  whiteboardsTablesMultiselectDeletionFix: {
    enabled: false,
    flag: 'cc-whiteboards-tables-multiselect-deletion-fix',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables explicit user-action undo boundaries for Whiteboards tables.
   * https://switcheroo.atlassian.com/ui/gates/7fb80212-24d5-4af4-b6de-01eda2afa25f/key/cc-whiteboards-tables-undo-improvements
   */
  whiteboardsTablesUndoImprovements: {
    enabled: false,
    flag: 'cc-whiteboards-tables-undo-improvements',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables performance improvements for table creation, rendering, and resizing.
   * https://switcheroo.atlassian.com/ui/gates/10f2830f-749a-4a13-9a89-9215ae749a24/key/cc-whiteboards-tables-performance-improvements
   */
  whiteboardsTablesPerformanceImprovements: {
    enabled: false,
    flag: 'cc-whiteboards-tables-performance-improvements',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables custom font sizing for individual Whiteboards table cells.
   * https://switcheroo.atlassian.com/ui/gates/d9ed4148-ece4-4ac1-b60f-d3361914d852/key/cc-whiteboards-tables-better-font-sizing
   */
  whiteboardsTablesBetterFontSizing: {
    enabled: false,
    flag: 'cc-whiteboards-tables-better-font-sizing',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Uses focused DOM measurements and unfocused text metrics to prevent stale
   * editor wrapper heights from expanding manually resized table rows.
   * https://switcheroo.atlassian.com/ui/gates/1f8947f1-c268-46e4-909c-cd58fbf151bb/key/cc-whiteboards-tables-minimum-row-height-fix
   */
  whiteboardsTablesMinimumRowHeightFix: {
    enabled: false,
    flag: 'cc-whiteboards-tables-minimum-row-height-fix',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Allows narrow imported columns and manual resizing down to 40 px.
   * https://switcheroo.atlassian.com/ui/gates/604b63f7-fb44-4d4a-92e1-f7a720b069ee/key/cc-whiteboards-tables-minimum-column-width
   */
  whiteboardsTablesMinimumColumnWidth: {
    enabled: false,
    flag: 'cc-whiteboards-tables-minimum-column-width',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables table cell merge and split authoring. Existing merged-cell content
   * remains available regardless of this gate.
   * https://switcheroo.atlassian.com/ui/gates/9426cec6-70e2-4e2e-9f49-1b1c7ff1803f/key/cc-whiteboards-tables-merge-split-cells
   */
  whiteboardsTablesMergeSplitCells: {
    enabled: false,
    flag: 'cc-whiteboards-tables-merge-split-cells',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Kill switch for the large-board performance work behind the increased element
   * limit. Turning it off reverts the performance changes and, because it is a
   * prerequisite of the `increasedBoardElementLimit` experiment, drops the active
   * limit back to `ELEMENT_BOARD_LIMIT_10K` — a rollback with no code change.
   * https://switcheroo.atlassian.com/ui/gates/025f41ce-ed03-485f-bde9-bf5c0066f97c/key/cc-whiteboards-large-board-performance-fixes
   */
  largeBoardPerformanceFixes: {
    enabled: false,
    flag: 'cc-whiteboards-large-board-performance-fixes',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Kill switch for the improved Miro/Mural import behavior that renders text
   * inside a shape (when it fits within the shape bounds plus padding) instead
   * of emitting a separate shape element plus an overlaid text element. Because
   * it is a prerequisite of the `increasedBoardElementLimit` experiment, turning
   * it off drops the active limit back to `ELEMENT_BOARD_LIMIT_10K` — a rollback
   * with no code change.
   * https://switcheroo.atlassian.com/ui/gates/1f1def67-c575-4e4b-a9ca-99f0715ed4a5/key/cc-whiteboards-import-shape-improvement
   */
  importShapeImprovement: {
    enabled: false,
    flag: 'cc-whiteboards-import-shape-improvement',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  // Controls Miro Grid mapping to native tables during PDF import.
  // https://switcheroo.atlassian.com/ui/gates/02a87d7b-c2b4-4810-9939-d2cf4be5f90c/key/cc-whiteboards-miro-grid-import
  miroGridImport: {
    enabled: false,
    flag: 'cc-whiteboards-miro-grid-import',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Umbrella gate for the PDF fidelity readability workstream. Enables all
   * fixes that improve text legibility after import (spurious spaces, missing
   * spaces, and future readability fixes in the same family).
   * https://switcheroo.atlassian.com/ui/gates/5ebc1e26-c695-4ddd-abf3-d59bedfbd4a5/key/cc-whiteboards-pdf-fidelity-readability-issues
   */
  pdfFidelityReadabilityIssues: {
    enabled: false,
    flag: 'cc-whiteboards-pdf-fidelity-readability-issues',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Enables updated Miro sticky palettes with manual light/dark mappings in browser imports.
   * https://switcheroo.atlassian.com/ui/gates/a0145b4c-6806-4501-90ed-4a01856ad158/key/cc-whiteboards-miro-mural-import-colours
   */
  miroImportColours: {
    enabled: false,
    flag: 'cc-whiteboards-miro-mural-import-colours',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Shows a product-wide announcement banner about the NCS refactor, pointing
   * Atlassians to the team Slack channel if they run into issues. Will only ever
   * be enabled in dogfooding
   * https://switcheroo.atlassian.com/ui/gates/f21af373-15a4-4b3c-bd6d-6e1b40b0dcce/key/canvas_ncs_refactor_announcement_banner
   */
  ncsRefactorAnnouncementBanner: {
    enabled: false,
    flag: 'canvas_ncs_refactor_announcement_banner',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Gates the Create with Rovo Jira issue Place and Apply stream: advertising the
   * `data-jira-key` Place capability to Rovo and Jira key ingestion.
   * https://switcheroo.atlassian.com/ui/gates/79089815-f773-4e53-831f-1fbcf322b08a/key/cc-whiteboards-cwr-jira-issues-place-apply
   */
  cwrJiraIssuesPlaceApply: {
    enabled: false,
    flag: 'cc-whiteboards-cwr-jira-issues-place-apply',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Gates the Create with Rovo Jira smart connector Place and Apply stream: advertising
   * the `data-jira-link-id` Apply capability to Rovo and smart connectors resolved
   * from Jira issue links.
   * https://switcheroo.atlassian.com/ui/gates/9a8ddbb3-ca92-461d-8cfc-73fe0be8df5e/key/cc-whiteboards-cwr-jira-connectors-place-apply
   */
  cwrJiraConnectorsPlaceApply: {
    enabled: false,
    flag: 'cc-whiteboards-cwr-jira-connectors-place-apply',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Adds an embedded background to private-mode anonymous avatars so their white
   * glyph remains visible when host avatar chrome is neutral.
   * https://switcheroo.atlassian.com/ui/gates/c8dc2652-4fb1-4402-8fce-21c1cca7bba3/key/canvas-add-anonymous-background
   */
  'canvas-add-anonymous-background': {
    enabled: false,
    flag: 'canvas-add-anonymous-background',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Disables the constraint solver from forcing rectangles with 2 or more children being converted to sections.
   * https://switcheroo.atlassian.com/ui/gates/b5a198fa-2ec9-44b1-9288-e4d92a2c3531/key/cc-whiteboards-disable-section-auto-conversion_
   */
  disableAutoRectAIFCIngestion: {
    enabled: false,
    flag: 'cc-whiteboards-disable-section-auto-conversion_',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Reports mentions that Confluence would otherwise never see — self mentions, mentions whose
   * permission check failed, and repeats in other elements — so they can be audit logged, and
   * sends the element each mention was made in. Shared with the Confluence monolith, which gates
   * the audit event itself on the same key.
   * https://switcheroo.atlassian.com/ui/gates/46704460-e2fb-48ad-8ab7-6887f503fbb4/key/cc_whiteboards_activity_logs
   */
  whiteboardsActivityLogs: {
    enabled: false,
    flag: 'cc_whiteboards_activity_logs',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Gate for awareness measurements in the base adaptive-awareness infrastructure.
   * https://switcheroo.atlassian.com/ui/gates/bafef141-b1bb-479a-8b75-4443e8c1bc2c/key/cc-content-types-adaptive-awareness-throttling
   */
  'cc-content-types-adaptive-awareness-throttling': {
    enabled: false,
    flag: 'cc-content-types-adaptive-awareness-throttling',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
  /**
   * Inserts a space where CWR ingestion joins a wrapped SVG text line onto the previous paragraph
   * https://switcheroo.atlassian.com/ui/gates/de1b4bb4-fdd7-4e86-8b1a-fe4f94c64ab9/key/cc-whiteboards-cwr-wrapped-line-separator
   */
  cwrWrappedLineSeparator: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-cwr-wrapped-line-separator',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Adds third-party Smart Link analytics to whiteboards for the 3P MAU definition
   * https://switcheroo.atlassian.com/ui/gates/ba8e792e-d2a6-4e08-8076-4de6dc5bb42c/key/cc-whiteboards-smart-link-3p-analytics
   */
  smartLink3pAnalytics: {
    source: FeatureFlagSource.Statsig,
    enabled: false,
    flag: 'cc-whiteboards-smart-link-3p-analytics',
    targetApp: [TargetApp.CanvasWeb],
  },
  /**
   * Gates adaptive persistent update batching on the NCS collaboration path.
   * https://switcheroo.atlassian.com/ui/gates/00e3533a-10fc-489f-936e-0397f5191c2f/key/cc-content-types-adaptive-persistent-batching
   */
  'cc-content-types-adaptive-persistent-batching': {
    enabled: false,
    flag: 'cc-content-types-adaptive-persistent-batching',
    source: FeatureFlagSource.Statsig,
    targetApp: [TargetApp.CanvasWeb, TargetApp.DatabasesWeb],
  },
} as const satisfies Record<string, ClientFeatureFlag>;

type Mutable<T> = { -readonly [P in keyof T]: Mutable<T[P]> };

// Resolved feature flags without overrides applied.
export const resolvedFeatureFlagState: Mutable<ClientFeatureFlags> =
  defaultFeatureFlagState;

/**
 * Initial state is set using the `initializeStatsigFlagState` in statsig.ts.
 */
export const currentFeatureFlagState: Mutable<ClientFeatureFlags> = proxy(
  defaultFeatureFlagState,
);

/**
 * If true, supplied feature flag is enabled.
 * @description Ensure you check this value after app boot.
 * @param flag
 * @returns
 */
export const isFeatureFlagEnabled = (flag: ClientFeatureFlagName) => {
  /**
   * The session init handlers are called before we get FF values from statsig,
   * hence using environments to control the behaviour.
   */
  if (flag === 'explicitSPANavigation') {
    return true;
  }

  return Boolean(currentFeatureFlagState[flag].enabled);
};

export const resetFeatureFlagState = () => {
  for (const featureFlag of Object.keys(
    defaultFeatureFlagState,
  ) as (keyof typeof defaultFeatureFlagState)[]) {
    currentFeatureFlagState[featureFlag] = defaultFeatureFlagState[featureFlag];
  }
};

export type FeatureFlagOverrides = Record<string, boolean>;

export const setFeatureFlagOverride = (featureFlagOverrides: FeatureFlagOverrides) => {
  for (const flag of Object.keys(defaultFeatureFlagState) as ClientFeatureFlagName[]) {
    currentFeatureFlagState[flag].enabled =
      featureFlagOverrides[flag] ?? defaultFeatureFlagState[flag].enabled;
  }
};
